<template>
  <div class="settings-block shared-reports" :key="route.path">
    <div class="settings-block__title">
      <div class="settings-block__title-text">{{ t('List of Shared Reports') }}</div>
      <a
        :href="`https://docs.bemob.com/${locale}/shared-report`"
        class="panel-title-link"
        target="_blank"
      >
        <ui-icon :color="$colors.green" class="mr-6" icon="fas fa-question-circle"></ui-icon>

        <span class="read-guide">
          {{ t('Read Guide') }}
        </span>
      </a>
    </div>
    <div>
      <div
        class="pb-8"
        v-html="
          t(
            'The <b>Shared Reports</b> feature enables clients to create reports for third parties without granting access to their BeMob account.<br>Reports can be generated for all general and detailed data, excluding <b>Postbacks, Conversions, Live Visits, Live Clicks and Errors</b>.<br>To share a report, navigate to the desired reporting tab and click the {0}.<br>The generated link can be shared immediately.<br>Owners and Admins can create up to 100 shared reports, and all shared report links will be stored in the section for easy access.',
            [`<i class='v-icon icon-share fas fa-share-square'></i>`],
          )
        "
      />
      <ui-table
        :headers="headers"
        :items="filteredRows"
        class="main-table"
        :virtualize="false"
        :rows-per-page-count="10"
      >
        <template #item.created="{ item }">
          {{ dateLocal(item.created).format('DD MMM YYYY HH:mm') }}
        </template>
        <template #item.name="{ item }">
          <v-tooltip location="top">
            <template #activator="{ props }">
              <div class="report-name" v-bind="props">{{ item.name }}</div>
            </template>
            <span>{{ item.name }}</span>
          </v-tooltip>
        </template>
        <template #item.link="{ item }">
          <input
            class="table-hidden-input"
            type="text"
            :ref="(element) => setLinkInputRef(item.id, element)"
            :value="item.link"
          />
          <div class="d-flex">
            <a :href="item.link" class="table-link" target="_blank">
              {{ item.link }}
            </a>
            <button
              class="table-copy-link"
              type="button"
              :style="{ color: 'var(--color-green)' }"
              @click="copyLink(item.id)"
            >
              <v-tooltip
                activator="parent"
                :open-on-hover="false"
                :modelValue="copiedLinkId === item.id"
                location="top"
                center
              >
                <template #activator>
                  <div class="table-copy-link-activator"></div>
                </template>
                <span>{{ t('Copied!') }}</span>
              </v-tooltip>
              <ui-icon
                :color="$colors.green"
                class="mr-4"
                :style="{ 'vertical-align': 'baseline' }"
                icon="fas fa-copy"
              ></ui-icon>
            </button>
          </div>
        </template>
        <template #item.availableUntil="{ item }">
          <div v-if="!item.availableUntil">
            {{ t('No Time Limits') }}
          </div>
          <div class="expired-date" v-else-if="isDateExpired(item.availableUntil)">
            {{ t('Expired') }}
          </div>
          <div v-else>
            {{ dateLocal(item.availableUntil).format('DD MMM YYYY HH:mm') }}
          </div>
        </template>
        <template #item.actions="{ item }">
          <div class="table-actions">
            <div v-if="!isDateExpired(item.availableUntil)">
              <button
                v-if="item.status === 'active'"
                class="table-edit mr-16"
                type="button"
                :style="{ color: 'var(--color-red)' }"
                @click="confirmChangeStatus(item)"
              >
                <ui-icon
                  :color="$colors.red"
                  class="mr-4"
                  :style="{ 'vertical-align': 'baseline' }"
                  icon="fas fa-square"
                ></ui-icon>
                {{ t('Stop') }}
              </button>
              <button
                v-if="item.status === 'inactive'"
                class="table-edit mr-16"
                type="button"
                :style="{ color: 'var(--color-green)' }"
                @click="confirmChangeStatus(item)"
              >
                <ui-icon
                  :color="$colors.green"
                  class="mr-4"
                  :style="{ 'vertical-align': 'baseline' }"
                  icon="fas fa-play"
                ></ui-icon>
                {{ t('Resume') }}
              </button>
            </div>
            <button
              class="table-edit"
              type="button"
              :style="{ color: 'var(--color-green)' }"
              @click="editReport(item)"
            >
              <ui-icon
                :color="$colors.green"
                class="mr-4"
                :style="{ 'vertical-align': 'baseline' }"
                icon="fas fa-edit"
              ></ui-icon>
              {{ t('Edit') }}
            </button>
            <v-menu
              content-class="table-menu"
              :transition="false"
              location="bottom end"
              :offset="[5, 19]"
            >
              <template #activator="{ props }">
                <button
                  type="button"
                  class="table-submenu-activator ml-16 mr-16"
                  v-bind="props"
                >
                  <ui-icon size="13" :color="$colors.green" icon="fas fa-ellipsis-h" />
                </button>
              </template>
              <div class="table-submenu">
                <button
                  class="table-submenu-button"
                  type="button"
                  @click="duplicateReport(item)"
                >
                  {{ t('Duplicate') }}
                  <ui-icon
                    class="ml-4"
                    :style="{ 'vertical-align': 'baseline' }"
                    icon="fas fa-window-restore"
                  />
                </button>
                <button
                  type="button"
                  class="table-submenu-button table-submenu-button--remove"
                  @click="confirmDeleteReport(item.id)"
                >
                  {{ t('Delete') }}
                  <ui-icon
                    :color="$colors.red"
                    class="ml-4"
                    :style="{ 'vertical-align': 'baseline' }"
                    icon="fas fa-trash-alt"
                  />
                </button>
              </div>
            </v-menu>
          </div>
        </template>
      </ui-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import _ from 'lodash';
import moment from 'moment';
import {
  deleteSharedReportApiRequest,
  getSharedReportsApiRequest,
  putSharedReportApiRequest,
} from '@/services/api/shared-reports-api';
import config from '@/config';
import { useColors, useI18n } from '@/composables';
import $emitter from '@/services/emitter';

type SharedReportRow = {
  created: string;
  name: string;
  link: string;
  id: string;
  actions: string;
  status: string;
  showDate: boolean;
  queryConditions: unknown;
  availableUntil: string | null;
  updated: string;
  updatedBy: string;
  searchBy: unknown;
  timezone: string;
};

const props = defineProps<{
  isTabActive?: boolean;
}>();

const route = useRoute();
const { t, locale } = useI18n();
const $colors = useColors();

const search = ref('');
const rows = ref<unknown[]>([]);
const copiedLinkId = ref<string | null>(null);
const linkInputRefs = ref<Record<string, HTMLInputElement>>({});

const sortItems = (items: SharedReportRow[], key: string, type?: boolean) => {
  return _.orderBy(
    items,
    (item) => {
      if (key === 'created') {
        return moment(item.created).format('YYYYMMDD HH:mm:ss');
      }
      return item[key as keyof SharedReportRow];
    },
    [type ? 'asc' : 'desc'],
  );
};

const isDateExpired = (from: string, to = moment()) => {
  return dateLocal(to).isAfter(dateLocal(from));
};

const dateLocal = (date: string | moment.Moment) => {
  return moment(moment.utc(date).toDate()).local();
};

const tableRows = computed(() => {
  if (!rows.value) return [];
  return rows.value.map((row: Record<string, unknown>) => ({
    created: row.created,
    name: row.name,
    link: `${config.sharedReportsURL}/${row.id}`,
    id: row.id,
    actions: '',
    status: row.status,
    showDate: row.showDate,
    queryConditions: row.queryConditions,
    availableUntil: row.availableUntil,
    updated: row.updated,
    updatedBy: row.updatedBy,
    searchBy: row.searchBy,
    timezone: row.timezone,
  })) as SharedReportRow[];
});

const filteredRows = computed(() =>
  sortItems(
    tableRows.value.filter(({ name }) =>
      name.toLowerCase().startsWith(search.value.toLowerCase()),
    ),
    'created',
  ),
);

const headers = computed(() => [
  {
    title: t('Creation Date'),
    value: 'created',
    sortable: true,
  },
  {
    title: t('Name'),
    value: 'name',
    sortable: true,
  },
  {
    title: t('Link'),
    value: 'link',
    sortable: false,
  },
  {
    title: t('Available Until'),
    value: 'availableUntil',
    sortable: false,
  },
  {
    value: 'actions',
    sortable: false,
  },
]);

const setLinkInputRef = (id: string, element: unknown) => {
  if (element) {
    linkInputRefs.value[id] = element as HTMLInputElement;
  }
};

const confirmChangeStatus = (data: SharedReportRow) => {
  if (isDateExpired(data.availableUntil)) {
    void loadSharedReports();
  }

  const modalChangeStatusLabel =
    data.status === 'inactive'
      ? t('The access to report for the third party will be restored.')
      : t('The access to report for the third party will be blocked.');

  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: modalChangeStatusLabel,
      onConfirm: async () => {
        const { id, ...restParams } = data;
        const status = restParams.status === 'inactive' ? 'active' : 'inactive';
        await putSharedReportApiRequest(id, { ...restParams, status });
        await loadSharedReports();
      },
    },
  });
};

const editReport = (data: SharedReportRow) => {
  $emitter.emit('open-dialog', {
    name: 'ShareReportDialog',
    data: {
      action: 'edit',
      formData: data,
    },
  });
};

const duplicateReport = (data: SharedReportRow) => {
  $emitter.emit('open-dialog', {
    name: 'ShareReportDialog',
    data: {
      action: 'duplicate',
      formData: data,
    },
  });
};

const confirmDeleteReport = (id: string) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('Deleted report can not be restored.'),
      onConfirm: async () => {
        await deleteSharedReportApiRequest(id);
        await loadSharedReports();
      },
    },
  });
};

const copyLink = (id: string) => {
  const input = linkInputRefs.value[id];
  input.select();
  document.execCommand('copy');
  copiedLinkId.value = id;
  setTimeout(() => {
    copiedLinkId.value = null;
  }, 1500);
};

const loadSharedReports = async () => {
  rows.value = await getSharedReportsApiRequest();
};

watch(
  () => props.isTabActive,
  () => {
    copiedLinkId.value = null;
  },
);

onMounted(() => {
  void loadSharedReports();
  $emitter.on('load-shared-reports', loadSharedReports);
});

onBeforeUnmount(() => {
  $emitter.off('load-shared-reports', loadSharedReports);
});
</script>

<style lang="scss" scoped>

@import '@/styles/pages/settings/multi-user-access';

.report-name {
  text-overflow: ellipsis;
  overflow: hidden;
}

:deep(.icon-share) {
  font-size: 12px !important;
  margin-bottom: 2px;
}

.search {
  margin-top: 16px;
  width: 200px;
}

.expired-date {
  color: var(--color-red);
}

.main-table {
  overflow: auto;

  :deep(.ui-table__inner) {
    min-width: 900px;
    table-layout: fixed;
  }

  :deep(.table-header > .table-cell),
  :deep(.table-body .table-row > .table-cell) {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding: 8px 5px 8px 0;
    text-align: left;
  }

  :deep(.table-header > .table-cell:nth-child(1)),
  :deep(.table-body .table-row > .table-cell:nth-child(1)) {
    width: 20% !important;
  }
  :deep(.table-header > .table-cell:nth-child(2)),
  :deep(.table-body .table-row > .table-cell:nth-child(2)) {
    width: 20% !important;
  }
  :deep(.table-header > .table-cell:nth-child(3)),
  :deep(.table-body .table-row > .table-cell:nth-child(3)) {
    width: 30% !important;
  }
  :deep(.table-header > .table-cell:nth-child(4)),
  :deep(.table-body .table-row > .table-cell:nth-child(4)) {
    width: 20% !important;
  }
  :deep(.table-header > .table-cell:nth-child(5)),
  :deep(.table-body .table-row > .table-cell:nth-child(5)) {
    width: 190px !important;
  }
  :deep(.table-header > .table-cell:nth-child(5):lang(ru)),
  :deep(.table-body .table-row > .table-cell:nth-child(5):lang(ru)) {
    width: 242px !important;
  }
  :deep(.table-header > .table-cell:nth-child(5):lang(es)),
  :deep(.table-body .table-row > .table-cell:nth-child(5):lang(es)) {
    width: 225px !important;

    .table-actions button {
      display: flex;
    }
  }
}

.table-link {
  color: var(--color-blue);
  text-decoration: none;
  text-overflow: ellipsis;
  overflow: hidden;
}

.table-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  button {
    align-items: center;
    display: flex;
  }
}

.table-hidden-input {
  position: absolute;
  opacity: 0;
  z-index: -1000;
}

.table-copy-link {
  position: relative;
  left: 2px;
  padding-right: 15px;
}

.table-copy-link-activator {
  position: absolute;
  width: 100%;
  height: 0;
}


.table-submenu{
  background: var(--color-white);
  border-radius: 8px!important;
  display: flex;
  flex-direction:column;
}

.table-buttons{
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.table-submenu-button{
  display: flex;
  width: 188px;
  justify-content: space-between;
  padding:8px;
  align-items: center;
  border-bottom: 1px solid var(--color-green20);
}

.table-submenu-button--remove{
  border-bottom: none;
  color:var(--color-red);
}

.table-submenu-activator{
}

.table-submenu-inside{
  background: var(--color-white);
}
</style>
